import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { isValidRating } from "./rating";
import {
  DEFAULT_VIEWER,
  normaliseTopic,
  type Decision,
  type Dimension,
  type DimensionId,
  type Instance,
  type InstanceId,
  type Person,
  type PersonId,
  type PersonTopic,
  type RatingEvent,
  type Tag,
  type TagId,
  type Topic,
  type TopicId,
} from "./model";
import type {
  InstanceQuery,
  NewInstance,
  NewPerson,
  PersonPatch,
  PersonTopicQuery,
  RatingEventQuery,
  Repository,
  RepositoryDump,
  TagInput,
  TagQuery,
} from "./repository";

interface Schema extends DBSchema {
  people: { key: PersonId; value: Person; indexes: { by_created: number } };
  dimensions: { key: DimensionId; value: Dimension };
  tags: {
    key: TagId;
    value: Tag;
    indexes: {
      by_person: PersonId;
      by_viewer_dim: [string, DimensionId];
      by_triple: [string, PersonId, DimensionId];
    };
  };
  decisions: { key: string; value: Decision; indexes: { by_created: number } };
  config: { key: string; value: string };
  instances: { key: InstanceId; value: Instance; indexes: { by_person: PersonId } };
  rating_events: {
    key: string;
    value: RatingEvent;
    indexes: { by_person: PersonId };
  };
  topics: { key: TopicId; value: Topic; indexes: { by_key: string } };
  person_topics: {
    key: string;
    value: PersonTopic;
    indexes: { by_person: PersonId; by_topic: TopicId };
  };
}

const DB_NAME = "trustlens";
const DB_VERSION = 11;

// Rating changes within this window collapse into one history entry, so
// changing your mind (5 then 4) is a single settled entry, not two.
const HISTORY_COLLAPSE_MS = 15_000;

// The original six seeded names, kept here only for migrations of stores created
// before the pack/backbone model. New stores seed from GENERIC_PACK instead. The
// definitions help an old store gain calibrated definitions on upgrade to v9;
// canonical_key stays null for those (existing stores are otherwise untouched).
const LEGACY_SEED_NAMES = [
  "Judgement",
  "Integrity",
  "Origination instinct",
  "Analytical rigour",
  "Reliability",
  "Discretion",
] as const;

const LEGACY_DEFINITIONS: Record<string, string> = {
  Judgement: "Quality of calls under uncertainty.",
  Integrity: "Honesty and straight dealing when it costs them.",
  "Origination instinct": "Finds opportunities others miss.",
  "Analytical rigour": "Depth and honesty of the underlying analysis.",
  Reliability: "Does what they said, on time, without chasing.",
  Discretion: "Keeps confidences and is sound about what to share.",
};

// Map a legacy 0..10000 budget weight onto the new 1..5 rating, order preserved.
function weightToRating(weight: number): number {
  return Math.max(1, Math.min(5, Math.round(weight / 2000)));
}

function id(): string {
  return crypto.randomUUID();
}

function now(): number {
  return Date.now();
}

export class IndexedDBRepository implements Repository {
  private dbp: Promise<IDBPDatabase<Schema>>;

  // `dbName` is overridable so tests (with fake-indexeddb/auto patching the
  // global) get an isolated store per case. Production uses the default.
  constructor(dbName: string = DB_NAME) {
    this.dbp = openDB<Schema>(dbName, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) {
          const people = db.createObjectStore("people", { keyPath: "id" });
          people.createIndex("by_created", "created_at");

          db.createObjectStore("dimensions", { keyPath: "id" });

          const tags = db.createObjectStore("tags", { keyPath: "id" });
          tags.createIndex("by_person", "person_id");
          tags.createIndex("by_viewer_dim", ["viewer_id", "dimension_id"]);
          tags.createIndex(
            "by_triple",
            ["viewer_id", "person_id", "dimension_id"],
            { unique: true },
          );

          const decisions = db.createObjectStore("decisions", { keyPath: "id" });
          decisions.createIndex("by_created", "created_at");
          // No dimensions are seeded on first run. A fresh store has zero
          // dimensions so the app can lead the user to choose a purpose from the
          // empty state (see the purpose chooser in Assemble / the person view).
        }
        if (oldVersion < 2) {
          // Out-of-line keys: stored via put(value, key).
          db.createObjectStore("config");
        }
        if (oldVersion >= 1 && oldVersion < 3) {
          // Migrate legacy budget weights to 1..5 ratings.
          const store = tx.objectStore("tags");
          let cursor = await store.openCursor();
          while (cursor) {
            const value = cursor.value as Tag & { weight?: number };
            if (value.rating === undefined && value.weight !== undefined) {
              value.rating = weightToRating(value.weight);
              delete value.weight;
              await cursor.update(value);
            }
            cursor = await cursor.continue();
          }
        }
        if (oldVersion >= 1 && oldVersion < 4) {
          // Backfill dimension display order: seeded names keep their seed
          // position, anything else is appended after.
          const store = tx.objectStore("dimensions");
          let extra = LEGACY_SEED_NAMES.length;
          let cursor = await store.openCursor();
          while (cursor) {
            const value = cursor.value as Dimension & { order?: number };
            if (value.order === undefined) {
              const idx = LEGACY_SEED_NAMES.indexOf(value.name as never);
              value.order = idx >= 0 ? idx : extra++;
              await cursor.update(value);
            }
            cursor = await cursor.continue();
          }
        }
        if (oldVersion < 5) {
          if (!db.objectStoreNames.contains("instances")) {
            const inst = db.createObjectStore("instances", { keyPath: "id" });
            inst.createIndex("by_person", "person_id");
          }
        }
        if (oldVersion < 6) {
          const events = db.objectStoreNames.contains("rating_events")
            ? null
            : db.createObjectStore("rating_events", { keyPath: "id" });
          events?.createIndex("by_person", "person_id");
        }
        if (oldVersion >= 1 && oldVersion < 8) {
          // Decisions now carry the people they drew on. Backfill an empty list
          // so older decisions read cleanly as "referenced no one recorded".
          const store = tx.objectStore("decisions");
          let cursor = await store.openCursor();
          while (cursor) {
            const d = cursor.value as Decision & { referenced_person_ids?: PersonId[] };
            if (d.referenced_person_ids === undefined) {
              d.referenced_person_ids = [];
              await cursor.update(d);
            }
            cursor = await cursor.continue();
          }
        }
        if (oldVersion >= 1 && oldVersion < 9) {
          // Dimensions gained a one-line definition and a nullable canonical_key.
          // Backfill: match a legacy seed name to its definition, else "". Leave
          // canonical_key null on existing dimensions (subjective-only); the user
          // can map them later. Existing stores are otherwise untouched.
          const store = tx.objectStore("dimensions");
          let cursor = await store.openCursor();
          while (cursor) {
            const d = cursor.value as Dimension & {
              definition?: string;
              canonical_key?: string | null;
            };
            let changed = false;
            if (d.definition === undefined) {
              d.definition = LEGACY_DEFINITIONS[d.name] ?? "";
              changed = true;
            }
            if (d.canonical_key === undefined) {
              d.canonical_key = null;
              changed = true;
            }
            if (changed) await cursor.update(d);
            cursor = await cursor.continue();
          }
        }
        if (oldVersion >= 1 && oldVersion < 10) {
          // Dimensions gained a purposes list (the pack[s] they came from). Old
          // dimensions predate purpose packs, so backfill an empty list.
          const store = tx.objectStore("dimensions");
          let cursor = await store.openCursor();
          while (cursor) {
            const d = cursor.value as Dimension & { purposes?: string[] };
            if (d.purposes === undefined) {
              d.purposes = [];
              await cursor.update(d);
            }
            cursor = await cursor.continue();
          }
        }
        if (oldVersion < 11) {
          // Domain expertise: a separate axis. A controlled vocabulary (topics,
          // unique by normalised key) and per-person flags (person_topics).
          if (!db.objectStoreNames.contains("topics")) {
            const topics = db.createObjectStore("topics", { keyPath: "id" });
            topics.createIndex("by_key", "key", { unique: true });
          }
          if (!db.objectStoreNames.contains("person_topics")) {
            const pt = db.createObjectStore("person_topics", { keyPath: "id" });
            pt.createIndex("by_person", "person_id");
            pt.createIndex("by_topic", "topic_id");
          }
        }
        if (oldVersion >= 1 && oldVersion < 6) {
          // Seed one initial history event per existing rating so people are
          // not blank.
          const tagStore = tx.objectStore("tags");
          const eventStore = tx.objectStore("rating_events");
          let cursor = await tagStore.openCursor();
          while (cursor) {
            const t = cursor.value as Tag;
            await eventStore.add({
              id: id(),
              viewer_id: t.viewer_id,
              person_id: t.person_id,
              dimension_id: t.dimension_id,
              rating: t.rating,
              rationale: t.rationale,
              at: t.created_at,
            });
            cursor = await cursor.continue();
          }
        }
      },
    });
  }

  private db(): Promise<IDBPDatabase<Schema>> {
    return this.dbp;
  }

  async addPerson(input: NewPerson): Promise<Person> {
    const db = await this.db();
    const person: Person = {
      id: id(),
      target_key: input.target_key ?? `local:${id()}`,
      display_name: input.display_name,
      relationship_context: input.relationship_context ?? "",
      source: input.source ?? "manual",
      created_at: now(),
    };
    if (input.email) person.email = input.email;
    await db.add("people", person);
    return person;
  }

  async updatePerson(personId: PersonId, patch: PersonPatch): Promise<Person> {
    const db = await this.db();
    const existing = await db.get("people", personId);
    if (!existing) throw new Error(`person not found: ${personId}`);
    const updated: Person = { ...existing, ...patch };
    await db.put("people", updated);
    return updated;
  }

  getPerson(personId: PersonId): Promise<Person | undefined> {
    return this.db().then((db) => db.get("people", personId));
  }

  async listPeople(): Promise<Person[]> {
    const db = await this.db();
    return db.getAllFromIndex("people", "by_created");
  }

  async deletePerson(personId: PersonId): Promise<void> {
    const db = await this.db();
    const tx = db.transaction(
      ["people", "tags", "instances", "rating_events", "person_topics"],
      "readwrite",
    );
    await tx.objectStore("people").delete(personId);
    for (const name of ["tags", "instances", "rating_events", "person_topics"] as const) {
      const s = tx.objectStore(name);
      for (const key of await s.index("by_person").getAllKeys(personId)) {
        await s.delete(key);
      }
    }
    await tx.done;
  }

  async listDimensions(): Promise<Dimension[]> {
    const db = await this.db();
    const all = await db.getAll("dimensions");
    return all.sort((a, b) => a.order - b.order);
  }

  async addDimension(
    name: string,
    definition = "",
    canonicalKey: string | null = null,
    purposes: string[] = [],
  ): Promise<Dimension> {
    const db = await this.db();
    const all = await db.getAll("dimensions");
    const order = all.reduce((m, d) => Math.max(m, d.order), -1) + 1;
    const dimension: Dimension = {
      id: id(),
      name,
      definition,
      order,
      canonical_key: canonicalKey,
      purposes,
    };
    await db.add("dimensions", dimension);
    return dimension;
  }

  async updateDimension(
    dimensionId: DimensionId,
    patch: { name?: string; definition?: string; canonical_key?: string | null },
  ): Promise<Dimension> {
    const db = await this.db();
    const existing = await db.get("dimensions", dimensionId);
    if (!existing) throw new Error(`dimension not found: ${dimensionId}`);
    const updated: Dimension = {
      ...existing,
      name: patch.name ?? existing.name,
      definition: patch.definition ?? existing.definition,
      // null is a meaningful value (clear the mapping), so distinguish "omitted"
      // (leave as is) from an explicit null.
      canonical_key:
        "canonical_key" in patch
          ? (patch.canonical_key ?? null)
          : existing.canonical_key,
    };
    await db.put("dimensions", updated);
    return updated;
  }

  async deleteDimension(dimensionId: DimensionId): Promise<void> {
    const db = await this.db();
    const tx = db.transaction(
      ["dimensions", "tags", "instances", "rating_events"],
      "readwrite",
    );
    await tx.objectStore("dimensions").delete(dimensionId);
    for (const name of ["tags", "instances", "rating_events"] as const) {
      let cursor = await tx.objectStore(name).openCursor();
      while (cursor) {
        if (cursor.value.dimension_id === dimensionId) await cursor.delete();
        cursor = await cursor.continue();
      }
    }
    await tx.done;
  }

  async upsertTag(input: TagInput): Promise<Tag> {
    if (!isValidRating(input.rating)) {
      throw new RangeError(`rating ${input.rating} is not an integer in 1..5`);
    }
    const db = await this.db();
    const viewer = input.viewer_id ?? DEFAULT_VIEWER;
    const tx = db.transaction(["tags", "rating_events"], "readwrite");
    const store = tx.objectStore("tags");

    const existing = await store
      .index("by_triple")
      .get([viewer, input.person_id, input.dimension_id]);

    const valueChanged = !existing || existing.rating !== input.rating;

    // Record history only when the rating VALUE changes (or is first set). Rapid
    // successive changes collapse into one settled entry.
    if (valueChanged) {
      const events = tx.objectStore("rating_events");
      const mine = (await events.getAll())
        .filter(
          (e) =>
            e.viewer_id === viewer &&
            e.person_id === input.person_id &&
            e.dimension_id === input.dimension_id,
        )
        .sort((a, b) => b.at - a.at);
      const latest = mine[0];
      const rationale = input.rationale ?? existing?.rationale ?? "";
      if (latest && now() - latest.at < HISTORY_COLLAPSE_MS) {
        await events.put({ ...latest, rating: input.rating, rationale, at: now() });
      } else {
        await events.add({
          id: id(),
          viewer_id: viewer,
          person_id: input.person_id,
          dimension_id: input.dimension_id,
          rating: input.rating,
          rationale,
          at: now(),
        });
      }
    }

    const tag: Tag = existing
      ? {
          ...existing,
          rating: input.rating,
          rationale: input.rationale ?? existing.rationale,
          // A new read resets the baseline, so events before it stop counting.
          read_at: valueChanged ? now() : (existing.read_at ?? existing.created_at),
          updated_at: now(),
        }
      : {
          id: id(),
          viewer_id: viewer,
          person_id: input.person_id,
          dimension_id: input.dimension_id,
          rating: input.rating,
          rationale: input.rationale ?? "",
          read_at: now(),
          created_at: now(),
          updated_at: now(),
        };
    await store.put(tag);
    await tx.done;
    return tag;
  }

  async addInstance(input: NewInstance): Promise<Instance> {
    if (!Number.isInteger(input.magnitude) || input.magnitude < 1 || input.magnitude > 3) {
      throw new RangeError(`magnitude ${input.magnitude} is not an integer in 1..3`);
    }
    const db = await this.db();
    const viewer = input.viewer_id ?? DEFAULT_VIEWER;
    const instance: Instance = {
      id: id(),
      viewer_id: viewer,
      person_id: input.person_id,
      dimension_id: input.dimension_id,
      valence: input.valence,
      magnitude: input.magnitude,
      note: input.note ?? "",
      occurred_at: input.occurred_at,
      created_at: now(),
    };
    await db.add("instances", instance);
    return instance;
  }

  async listInstances(query: InstanceQuery = {}): Promise<Instance[]> {
    const db = await this.db();
    const all = await db.getAll("instances");
    return all.filter(
      (i) =>
        (query.viewer_id === undefined || i.viewer_id === query.viewer_id) &&
        (query.person_id === undefined || i.person_id === query.person_id) &&
        (query.dimension_id === undefined || i.dimension_id === query.dimension_id),
    );
  }

  async deleteInstance(instanceId: InstanceId): Promise<void> {
    const db = await this.db();
    await db.delete("instances", instanceId);
  }

  async listRatingEvents(query: RatingEventQuery = {}): Promise<RatingEvent[]> {
    const db = await this.db();
    const all = await db.getAll("rating_events");
    return all.filter(
      (e) =>
        (query.viewer_id === undefined || e.viewer_id === query.viewer_id) &&
        (query.person_id === undefined || e.person_id === query.person_id) &&
        (query.dimension_id === undefined || e.dimension_id === query.dimension_id),
    );
  }

  async deleteRatingEvent(eventId: string): Promise<void> {
    const db = await this.db();
    await db.delete("rating_events", eventId);
  }

  async clearRatingEvents(personId: PersonId): Promise<void> {
    const db = await this.db();
    const tx = db.transaction("rating_events", "readwrite");
    const store = tx.objectStore("rating_events");
    for (const key of await store.index("by_person").getAllKeys(personId)) {
      await store.delete(key);
    }
    await tx.done;
  }

  getTag(tagId: TagId): Promise<Tag | undefined> {
    return this.db().then((db) => db.get("tags", tagId));
  }

  async listTags(query: TagQuery = {}): Promise<Tag[]> {
    const db = await this.db();
    const all = await db.getAll("tags");
    return all.filter(
      (t) =>
        (query.viewer_id === undefined || t.viewer_id === query.viewer_id) &&
        (query.person_id === undefined || t.person_id === query.person_id) &&
        (query.dimension_id === undefined || t.dimension_id === query.dimension_id),
    );
  }

  async deleteTag(tagId: TagId): Promise<void> {
    const db = await this.db();
    await db.delete("tags", tagId);
  }

  async addDecision(
    prompt: string,
    output: string,
    referencedPersonIds: PersonId[] = [],
  ): Promise<Decision> {
    const db = await this.db();
    const decision: Decision = {
      id: id(),
      prompt,
      output,
      referenced_person_ids: referencedPersonIds,
      created_at: now(),
    };
    await db.add("decisions", decision);
    return decision;
  }

  async listDecisions(): Promise<Decision[]> {
    const db = await this.db();
    return db.getAllFromIndex("decisions", "by_created");
  }

  async listTopics(): Promise<Topic[]> {
    const db = await this.db();
    return db.getAll("topics");
  }

  async addTopic(name: string, source: "curated" | "custom" = "custom"): Promise<Topic> {
    const key = normaliseTopic(name);
    const db = await this.db();
    // Dedup on entry: an existing topic with the same root is reused, so "Solar"
    // and "solar" do not split.
    const existing = await db.getFromIndex("topics", "by_key", key);
    if (existing) return existing;
    const topic: Topic = { id: id(), name: name.trim(), key, source, created_at: now() };
    await db.add("topics", topic);
    return topic;
  }

  async deleteTopic(topicId: TopicId): Promise<void> {
    const db = await this.db();
    const tx = db.transaction(["topics", "person_topics"], "readwrite");
    await tx.objectStore("topics").delete(topicId);
    const links = tx.objectStore("person_topics");
    for (const key of await links.index("by_topic").getAllKeys(topicId)) {
      await links.delete(key);
    }
    await tx.done;
  }

  async listPersonTopics(query: PersonTopicQuery = {}): Promise<PersonTopic[]> {
    const db = await this.db();
    const all = await db.getAll("person_topics");
    return all.filter(
      (pt) =>
        (query.person_id === undefined || pt.person_id === query.person_id) &&
        (query.topic_id === undefined || pt.topic_id === query.topic_id),
    );
  }

  async addPersonTopic(personId: PersonId, topicId: TopicId): Promise<PersonTopic> {
    const db = await this.db();
    const existing = (await db.getAllFromIndex("person_topics", "by_person", personId)).find(
      (pt) => pt.topic_id === topicId,
    );
    if (existing) return existing;
    const link: PersonTopic = { id: id(), person_id: personId, topic_id: topicId };
    await db.add("person_topics", link);
    return link;
  }

  async removePersonTopic(personId: PersonId, topicId: TopicId): Promise<void> {
    const db = await this.db();
    const tx = db.transaction("person_topics", "readwrite");
    const store = tx.objectStore("person_topics");
    for (const pt of await store.index("by_person").getAll(personId)) {
      if (pt.topic_id === topicId) await store.delete(pt.id);
    }
    await tx.done;
  }

  async getConfig(key: string): Promise<string | undefined> {
    const db = await this.db();
    return db.get("config", key);
  }

  async setConfig(key: string, value: string): Promise<void> {
    const db = await this.db();
    await db.put("config", value, key);
  }

  async exportAll(): Promise<RepositoryDump> {
    const db = await this.db();
    const [people, dimensions, tags, decisions, instances, ratingEvents, topics, personTopics] =
      await Promise.all([
        db.getAll("people"),
        db.getAll("dimensions"),
        db.getAll("tags"),
        db.getAll("decisions"),
        db.getAll("instances"),
        db.getAll("rating_events"),
        db.getAll("topics"),
        db.getAll("person_topics"),
      ]);
    return { people, dimensions, tags, decisions, instances, ratingEvents, topics, personTopics };
  }

  async resetAll(): Promise<void> {
    const db = await this.db();
    const stores = [...db.objectStoreNames];
    const tx = db.transaction(stores, "readwrite");
    await Promise.all(stores.map((s) => tx.objectStore(s).clear()));
    await tx.done;
    // Nothing is re-seeded: a reset returns the store to the true first-run
    // state (zero dimensions), so the purpose chooser leads again.
  }

  async importAll(dump: RepositoryDump): Promise<void> {
    const db = await this.db();
    const tx = db.transaction(
      ["people", "dimensions", "tags", "decisions", "instances", "rating_events", "topics", "person_topics"],
      "readwrite",
    );
    const people = tx.objectStore("people");
    const dimensions = tx.objectStore("dimensions");
    const tags = tx.objectStore("tags");
    const decisions = tx.objectStore("decisions");
    const instances = tx.objectStore("instances");
    const ratingEvents = tx.objectStore("rating_events");
    const topics = tx.objectStore("topics");
    const personTopics = tx.objectStore("person_topics");
    await Promise.all([
      people.clear(),
      dimensions.clear(),
      tags.clear(),
      decisions.clear(),
      instances.clear(),
      ratingEvents.clear(),
      topics.clear(),
      personTopics.clear(),
    ]);
    await Promise.all([
      ...dump.people.map((p) => people.put(p)),
      ...dump.dimensions.map((d) => dimensions.put(d)),
      ...dump.tags.map((t) => tags.put(t)),
      ...dump.decisions.map((d) => decisions.put(d)),
      ...(dump.instances ?? []).map((i) => instances.put(i)),
      ...(dump.ratingEvents ?? []).map((e) => ratingEvents.put(e)),
      ...(dump.topics ?? []).map((t) => topics.put(t)),
      ...(dump.personTopics ?? []).map((pt) => personTopics.put(pt)),
    ]);
    await tx.done;
  }
}
